import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslate } from "helpers";
import { WarehouseDetailProps } from "./types";
import { Grid } from "ui/components/Grid";
import { AvailableWidget, GridRef } from "ui/components/Grid/types";
import { generateGridFromTemplate } from "ui/components/Grid/helper";
import { GridTemplate, GRID_TEMPLATES } from "ui/components/Grid/templates";
import { WAREHOUSE_AVAILABLE_WIDGETS } from "./consts";
import WidgetEditCard from "ui/modules/contacts/pages/ContactDetailPage/components/ContactDetailCard/components/WidgetEditCard/WidgetEditCard";

const STORAGE_KEY = "warehouse-detail-state";

const WarehouseDetailCard: React.FC<WarehouseDetailProps> = (props) => {
  const { fleetWarehouseDetail, showAddWidgetModal, setShowAddWidgetModal } = props;
  const { t } = useTranslate();

  const gridRef = useRef<GridRef>(null);

  const [savedState] = useState(() =>
    JSON.parse(localStorage.getItem(STORAGE_KEY) || "null")
  );
  const lastLayoutsRef = useRef<any>(savedState?.layouts ?? null);

  const allWidgets = WAREHOUSE_AVAILABLE_WIDGETS(t, fleetWarehouseDetail);

  const initialUserWidgets = savedState?.userWidgets ?? allWidgets.map((w) => w.id);

  const [userWidgets, setUserWidgets] = useState<string[]>(initialUserWidgets);
  const [template, setTemplate] = useState<GridTemplate>(
    savedState?.template ?? "three-columns"
  );

  const templateConfig = useMemo(
    () => GRID_TEMPLATES.find((tpl) => tpl.id === template) || GRID_TEMPLATES[0],
    [template]
  );

  const { widgets, layouts } = useMemo(() => {
    const availableWidgets = userWidgets
      .map((id) => allWidgets.find((w) => w.id === id))
      .filter(Boolean) as AvailableWidget[];

    return generateGridFromTemplate(templateConfig, availableWidgets);
  }, [userWidgets, t, fleetWarehouseDetail, templateConfig]);

  const saveState = useCallback(
    (layoutsToSave?: any) => {
      if (layoutsToSave) lastLayoutsRef.current = layoutsToSave;
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          userWidgets,
          template,
          layouts: layoutsToSave ?? lastLayoutsRef.current ?? layouts,
        })
      );
    },
    [userWidgets, template, layouts]
  );

  useEffect(() => {
    if (fleetWarehouseDetail.id) saveState();
  }, [userWidgets, template, saveState, fleetWarehouseDetail.id]);

  const handleTemplateChange = useCallback(
    (newTemplate: GridTemplate) => {
      setTemplate(newTemplate);
      lastLayoutsRef.current = null;
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          userWidgets,
          template: newTemplate,
          layouts: null,
        })
      );
    },
    [userWidgets]
  );

  const handleEditWidgetSuccess = useCallback(() => {
    setShowAddWidgetModal(false);
  }, []);

  return (
    <>
      <Grid
        key={`${userWidgets.join(",")}-${template}-${fleetWarehouseDetail.id}`}
        ref={gridRef}
        initialWidgets={widgets}
        initialLayouts={lastLayoutsRef.current ?? layouts}
        onLayoutChange={(newLayouts) => saveState(newLayouts)}
      />

      <WidgetEditCard
        show={showAddWidgetModal}
        availableWidgets={allWidgets}
        userWidgets={userWidgets}
        setUserWidgets={setUserWidgets}
        template={template}
        onTemplateChange={handleTemplateChange}
        onSuccess={handleEditWidgetSuccess}
        onClose={() => setShowAddWidgetModal(false)}
      />
    </>
  );
};
export default memo(WarehouseDetailCard);
